import { useEffect, useState, useRef } from 'react';
import { 
  Users, 
  Clock, 
  MessageSquare, 
  Search, 
  Send,
  // User,
  Phone,
  Mail,
  // Calendar,
  ShoppingCart,
  // Package,
  // AlertTriangle,
  PauseCircle,
  PlayCircle,
  Bot,
  UserCog,
  // ExternalLink,
  Wand2,
  Loader,
  ChevronUp,
  ChevronDown,
  XCircle
} from 'lucide-react';
import CategoryFilter from '../components/CategoryFilter';
// import { Link } from 'react-router-dom';

interface ChatAssignment {
  id: string;
  user_id: string;
  seller_id: string;
  assigned_at: string;
  last_interaction: string;
  status: string;
  messages_count: number;
  user: {
    id: string;
    name: string;
    phone: string;
    email: string;
  };
}

interface Message {
  id: string;
  user_id: string;
  content: string;
  direction: 'incoming' | 'outgoing';
  created_at: string;
}

interface UserData {
  user: {
    id: string;
    name: string;
    email: string;
    phone: string;
    bot_enabled: boolean;
    atendimento: boolean;
    created_at: string;
    categoria?: string;
    descricao_inicial?: string;
    origem?: string;
  };
  messages: Message[];
  shoppingLists: any[];
}

export default function Atendimento() {
  const [myQueue, setMyQueue] = useState<ChatAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'aguardando' | 'em_atendimento'>('aguardando');
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'venda' | 'redirecionamento'>('all');
  const [categoryStats, setCategoryStats] = useState<{venda: number; redirecionamento: number}>({venda: 0, redirecionamento: 0});
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loadingUser, setLoadingUser] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendOnEnter, setSendOnEnter] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [aiValidating, setAiValidating] = useState(false);
  const [aiValidationResult, setAiValidationResult] = useState<{
    show: boolean;
    approved: boolean;
    message: string;
  } | null>(null);
  const [botPaused, setBotPaused] = useState(false);
  const [previousMessagesLength, setPreviousMessagesLength] = useState(0);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isTrainee, setIsTrainee] = useState(false);
  const [generatingTemplate, setGeneratingTemplate] = useState<string | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showShoppingLists, setShowShoppingLists] = useState(false);
  const [expandedLists, setExpandedLists] = useState<string[]>([]);
  const [creatingPaymentLink, setCreatingPaymentLink] = useState<string | null>(null);
  const [updatingListStatus, setUpdatingListStatus] = useState<string | null>(null);
  const [showCustomAmount, setShowCustomAmount] = useState<string | null>(null);
  const [customAmount, setCustomAmount] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const loadingQueueRef = useRef(false); // Prevenir múltiplas requisições simultâneas

  useEffect(() => {
    loadMyQueue();
    loadCurrentUser();
    loadCategoryStats();
    // ⚡ Atualizar a cada 5 segundos (otimizado)
    const interval = setInterval(() => {
      loadMyQueue();
      loadCategoryStats();
    }, 5000);
    return () => clearInterval(interval);
  }, [activeTab, selectedCategory]);

  const loadCurrentUser = () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setCurrentUser(user);
        setIsTrainee(user.role === 'trainee');
      }
    } catch (error) {
      console.error('Erro ao carregar usuário:', error);
    }
  };

  useEffect(() => {
    if (selectedUserId) {
      loadUserData(selectedUserId);
      // ⚡ Atualizar mensagens a cada 4 segundos (otimizado)
      const interval = setInterval(() => {
        loadUserData(selectedUserId);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [selectedUserId]);

  useEffect(() => {
    // Scroll apenas se houver novas mensagens
    if (userData?.messages) {
      const currentLength = userData.messages.length;
      if (currentLength > previousMessagesLength && previousMessagesLength > 0) {
        // Só faz scroll se realmente aumentou o número de mensagens
        scrollToBottom();
      }
      setPreviousMessagesLength(currentLength);
    }
  }, [userData?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadCategoryStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || window.location.origin}/api/stats/categorias`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.data) {
        const stats = data.data.reduce((acc: any, item: any) => {
          acc[item.categoria] = item.total_usuarios || 0;
          return acc;
        }, {venda: 0, redirecionamento: 0});
        setCategoryStats(stats);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const loadMyQueue = async () => {
    // ⚡ Prevenir múltiplas requisições simultâneas
    if (loadingQueueRef.current) {
      console.log('⏳ Já carregando fila, ignorando requisição duplicada');
      return;
    }
    
    try {
      loadingQueueRef.current = true;
      const token = localStorage.getItem('token');
      let url = `${import.meta.env.VITE_API_URL || window.location.origin}/api/chats/my-queue?filter=${activeTab}`;
      
      // Adicionar filtro de categoria se não for 'all'
      if (selectedCategory !== 'all') {
        url += `&categoria=${selectedCategory}`;
      }
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setMyQueue(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar fila:', error);
    } finally {
      setLoading(false);
      loadingQueueRef.current = false;
    }
  };

  const loadUserData = async (userId: string, isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoadingUser(true);
      }
      const token = localStorage.getItem('token');
      const res = await fetch(`${import.meta.env.VITE_API_URL || window.location.origin}/api/users/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setUserData(data.data);
        setBotPaused(!data.data.user.bot_enabled);
      }
    } catch (error) {
      console.error('Erro ao carregar dados do usuário:', error);
    } finally {
      if (isInitialLoad) {
        setLoadingUser(false);
      }
    }
  };

  const handleSelectUser = (userId: string) => {
    setSelectedUserId(userId);
    loadUserData(userId, true); // true = mostrar loading inicial
  };

  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (sendingMessage || aiValidating) return;
    
    if (!newMessage.trim() || !userData) return;

    // Validar mensagem com IA antes de enviar
    await validateMessageWithAI();
  };

  const validateMessageWithAI = async () => {
    if (!userData) return;

    setAiValidating(true);
    setAiValidationResult(null);

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;

      // Validar mensagem com IA
      const response = await fetch(`${apiUrl}/api/ai/validate-message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: newMessage
        })
      });

      const data = await response.json();

      if (data.success) {
        const validation = data.data;

        if (validation.valid) {
          // Mensagem aprovada - enviar
          setAiValidationResult({
            show: true,
            approved: true,
            message: 'Mensagem aprovada pela IA'
          });

          // Usar mensagem corrigida se houver
          const messageToSend = validation.corrected_message || newMessage;
          
          setTimeout(() => {
            sendMessageToUser(messageToSend);
            setAiValidationResult(null);
          }, 1000);
        } else {
          // Mensagem rejeitada
          const issues = validation.issues.join(', ');
          const suggestions = validation.suggestions.length > 0 
            ? '\n\nSugestões: ' + validation.suggestions.join(', ')
            : '';
          
          setAiValidationResult({
            show: true,
            approved: false,
            message: `Mensagem bloqueada\n\nProblemas: ${issues}${suggestions}${validation.corrected_message ? '\n\nMensagem sugerida:\n' + validation.corrected_message : ''}`
          });

          // Se houver mensagem corrigida, substituir automaticamente
          if (validation.corrected_message && validation.severity !== 'high') {
            setTimeout(() => {
              setNewMessage(validation.corrected_message);
              setAiValidationResult(null);
            }, 3000);
          } else if (validation.severity === 'high') {
            // Severidade alta: notificar supervisor se for trainee
            if (isTrainee) {
              await notifySupervisor(issues);
            }
            // Limpar após 5 segundos
            setTimeout(() => {
              setAiValidationResult(null);
            }, 5000);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao validar com IA:', error);
      // Em caso de erro, permitir envio (fallback)
      sendMessageToUser(newMessage);
    } finally {
      setAiValidating(false);
    }
  };

  const notifySupervisor = async (feedback: string) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;

      await fetch(`${apiUrl}/api/chat/notify-supervisor`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          trainee_id: currentUser.id,
          trainee_name: currentUser.name,
          customer_phone: userData?.user.phone,
          customer_name: userData?.user.name,
          blocked_message: newMessage,
          ai_feedback: feedback
        })
      });
    } catch (error) {
      console.error('Erro ao notificar supervisor:', error);
    }
  };

  const generateTemplateMessage = async (templateType: string) => {
    if (!userData) return;
    
    setGeneratingTemplate(templateType);
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;

      const response = await fetch(`${apiUrl}/api/ai/generate-message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          template_type: templateType,
          customer_name: userData.user.name,
          customer_phone: userData.user.phone,
          shopping_list_items: userData.shoppingLists?.[0]?.shopping_list_items || []
        })
      });

      const result = await response.json();
      if (result.success && result.data.message) {
        setNewMessage(result.data.message);
        setShowTemplates(false);
      } else {
        alert('Erro ao gerar mensagem');
      }
    } catch (error) {
      console.error('Erro ao gerar template:', error);
      alert('Erro ao gerar mensagem com IA');
    } finally {
      setGeneratingTemplate(null);
    }
  };

  const sendMessageToUser = async (msg: string) => {
    setSendingMessage(true);
    try {
      if (!msg.trim() || !userData) { setSendingMessage(false); return; }
      setNewMessage('');
      setAiValidationResult(null);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }

      // Atualiza UI imediatamente com ID temporário
      const tempId = `temp-${Date.now()}`;
      const message: Message = {
        id: tempId,
        user_id: userData.user.id,
        content: msg,
        direction: 'outgoing',
        created_at: new Date().toISOString()
      };
      
      const updatedMessages = [...userData.messages, message];
      setUserData({
        ...userData,
        messages: updatedMessages
      });

      // Dispara para Evolution/Instagram
      try {
        const response = await fetch('/api/setText', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            userId: userData.user.id,
            phone: userData.user.phone,
            msg: msg
          })
        });

        const result = await response.json();
        
        // Verificar se houve erro de janela do Instagram
        if (!result.success && (result.error === 'instagram_window_expired' || result.error === 'instagram_no_incoming_message')) {
          alert(result.message);
          // Remover mensagem temporária da UI
          setUserData({
            ...userData,
            messages: userData.messages.filter(m => m.id !== tempId)
          });
          setSendingMessage(false);
          return;
        }
      } catch (err) {
        console.error('Erro ao enviar mensagem:', err);
      }

      // Salva no histórico (banco)
      try {
        await fetch('/api/send-admin-message', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            user_id: userData.user.id,
            content: msg
          })
        });
      } catch (err) {
        console.error('Erro ao registrar mensagem manual no histórico:', err);
      }

      // Marca bot_enabled como false (atendimento manual)
      try {
        await fetch(`/api/users/${userData.user.id}/toggle-bot`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ enabled: false, atendimento: true })
        });
        setUserData(prev => prev ? ({
          ...prev,
          user: {
            ...prev.user,
            bot_enabled: false,
            atendimento: true
          }
        }) : prev);
        setBotPaused(true);
      } catch (err) {
        console.error('Erro ao marcar bot_enabled como false:', err);
      }

      // Atribuir atendente automaticamente ao enviar mensagem
      try {
        const token = localStorage.getItem('token');
        const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
        
        // Buscar ID do usuário logado
        const meResponse = await fetch(`${apiUrl}/api/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const meData = await meResponse.json();
        
        await fetch(`${apiUrl}/api/chats/assign`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            user_id: userData.user.id,
            seller_id: meData.id
          })
        });
      } catch (err) {
        console.error('Erro ao atribuir atendente:', err);
      }

      // Aguardar um pouco e recarregar para pegar a mensagem do servidor
      setTimeout(() => {
        if (selectedUserId) {
          loadUserData(selectedUserId);
        }
      }, 1000);
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const toggleBotPause = async () => {
    if (!userData) return;

    try {
      const newPausedState = !botPaused;
      setBotPaused(newPausedState);
      setUserData(prev => prev ? ({
        ...prev,
        user: {
          ...prev.user,
          bot_enabled: !prev.user.bot_enabled,
          atendimento: !prev.user.bot_enabled ? false : true
        }
      }) : prev);

      const response = await fetch(`${import.meta.env.VITE_API_URL || window.location.origin}/api/users/${userData.user.id}/toggle-bot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          enabled: !userData.user.bot_enabled,
          atendimento: !userData.user.bot_enabled ? false : true
        })
      });

      if (!response.ok) {
        console.error('Error toggling bot state:', await response.text());
        setBotPaused(!newPausedState);
      }
    } catch (error) {
      console.error('Error toggling bot:', error);
      setBotPaused(!botPaused);
    }
  };

  const handleCloseChat = async () => {
    if (!userData) return;
    
    const confirmed = confirm(`Deseja fechar o atendimento de ${userData.user.name}?\n\nIsso irá:\n- Remover o chat da sua fila\n- Reativar o bot para futuras interações\n- Limpar a atribuição de vendedor`);
    
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${import.meta.env.VITE_API_URL || window.location.origin}/api/chats/close`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ user_id: userData.user.id })
      });

      if (response.ok) {
        alert(`✅ Atendimento de ${userData.user.name} foi fechado com sucesso!`);
        setSelectedUserId(null);
        setUserData(null);
        loadMyQueue();
      } else {
        const error = await response.json();
        alert(`❌ Erro ao fechar atendimento: ${error.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Erro ao fechar atendimento:', error);
      alert('❌ Erro ao fechar atendimento. Tente novamente.');
    }
  };

  const getTimeSince = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'data inválida';
      
      const now = new Date();
      const diff = now.getTime() - date.getTime();
      
      if (diff < 0) return 'agora';
      
      const minutes = Math.floor(diff / (1000 * 60));
      
      if (minutes < 1) return 'agora';
      if (minutes < 60) return `${minutes}min atrás`;
      
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h atrás`;
      
      const days = Math.floor(hours / 24);
      return `${days}d atrás`;
    } catch (err) {
      return 'data inválida';
    }
  };

  // Funções de gerenciamento de listas
  const toggleListExpansion = (listId: string) => {
    setExpandedLists(prev => 
      prev.includes(listId) 
        ? prev.filter(id => id !== listId) 
        : [...prev, listId]
    );
  };

  const openPaymentLinkForm = (listId: string, items: any[]) => {
    const total = calculateListTotal(items);
    const totalBRL = Math.round(total * 5.83 * 100) / 100; // Conversão USD para BRL com 2 decimais
    setCustomAmount(totalBRL);
    setShowCustomAmount(listId);
  };

  const createPaymentLink = async (listId: string) => {
    try {
      setCreatingPaymentLink(listId);
      const token = localStorage.getItem('token');
      
      const payload: any = { seller_id: currentUser?.id };
      if (customAmount) {
        payload.amount_brl = customAmount;
      }

      const response = await fetch(`/api/shopping-lists/${listId}/payment-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        const link = result.data?.payment_link;
        alert(`Link de pagamento criado!\n\n${link}`);
        setShowCustomAmount(null);
        setCustomAmount(null);
        // Recarregar dados do usuário
        if (selectedUserId) loadUserData(selectedUserId);
      } else {
        const error = await response.json();
        alert(`Erro: ${error.error || 'Erro ao criar link'}`);
      }
    } catch (error) {
      console.error('Erro ao criar link de pagamento:', error);
      alert('Erro ao criar link de pagamento');
    } finally {
      setCreatingPaymentLink(null);
    }
  };

  const updateListStatus = async (listId: string, status: string) => {
    try {
      setUpdatingListStatus(listId);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/shopping-lists/${listId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ updates: { status } })
      });

      if (response.ok) {
        alert(`Lista marcada como ${status === 'completed' ? 'concluída' : status === 'abandoned' ? 'abandonada' : 'ativa'}!`);
        // Recarregar dados do usuário
        if (selectedUserId) loadUserData(selectedUserId);
      } else {
        alert('Erro ao atualizar status da lista');
      }
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      alert('Erro ao atualizar status da lista');
    } finally {
      setUpdatingListStatus(null);
    }
  };

  const calculateListTotal = (items: any[]) => {
    return items.reduce((total, item) => total + (item.product_price * item.quantity), 0);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredQueue = myQueue
    .filter(chat => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        chat.user.name?.toLowerCase().includes(search) ||
        chat.user.phone?.includes(searchTerm) ||
        chat.user.email?.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      // Ordenar por última interação (mais recente primeiro)
      const dateA = new Date(a.last_interaction).getTime();
      const dateB = new Date(b.last_interaction).getTime();
      return dateB - dateA;
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando atendimentos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* Lista de Conversas - Esquerda */}
      <div className="w-1/3 bg-white border-r border-gray-200 flex flex-col max-h-screen">
        <div className="p-4 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900 mb-3">Meus Atendimentos</h1>
          
          {/* Abas */}
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => setActiveTab('aguardando')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'aguardando'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Clock className="h-4 w-4 inline mr-2" />
              Aguardando
            </button>
            <button
              onClick={() => setActiveTab('em_atendimento')}
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'em_atendimento'
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <UserCog className="h-4 w-4 inline mr-2" />
              Em Atendimento
            </button>
          </div>

          {/* Filtro de Categoria */}
          <div className="mt-3">
            <CategoryFilter
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              stats={categoryStats}
            />
          </div>

          <div className="relative mt-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filteredQueue.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {searchTerm 
                  ? 'Nenhum resultado encontrado' 
                  : activeTab === 'aguardando'
                    ? 'Nenhum chat aguardando'
                    : 'Nenhum chat em atendimento'
                }
              </p>
            </div>
          ) : (
            filteredQueue.map((chat) => (
              <div
                key={chat.id}
                className={`p-3 border-b border-gray-200 hover:bg-gray-50 transition-colors ${
                  selectedUserId === chat.user_id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  {/* Avatar */}
                  <div 
                    onClick={() => handleSelectUser(chat.user_id)}
                    className="h-11 w-11 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-sm cursor-pointer"
                  >
                    <span className="text-base font-bold text-white">
                      {chat.user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  
                  {/* Info */}
                  <div 
                    onClick={() => handleSelectUser(chat.user_id)}
                    className="flex-1 min-w-0 cursor-pointer"
                  >
                    <h3 className="font-medium text-sm text-gray-900 truncate">{chat.user.name}</h3>
                    <p className="text-xs text-gray-600 truncate">{chat.user.phone}</p>
                    <div className="flex items-center mt-1 text-xs text-gray-500">
                      <Clock className="h-3 w-3 mr-1" />
                      {getTimeSince(chat.last_interaction)}
                    </div>
                  </div>
                  
                  {/* Badge de mensagens */}
                  {chat.messages_count > 0 && (
                    <div className="flex-shrink-0">
                      <span className="bg-blue-500 text-white text-xs rounded-full px-2 py-1 font-semibold">
                        {chat.messages_count}
                      </span>
                    </div>
                  )}
                  
                  {/* Botão Auto-Atribuir (apenas em aguardando) */}
                  {activeTab === 'aguardando' && (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          const token = localStorage.getItem('token');
                          const res = await fetch(`${import.meta.env.VITE_API_URL || window.location.origin}/api/users/${chat.user_id}`, {
                            method: 'PUT',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({ 
                              seller_id: currentUser?.id,
                              atendimento: true,
                              bot_enabled: false,
                              in_service: true
                            })
                          });
                          
                          if (res.ok) {
                            // ✅ Mudar para aba "em atendimento" automaticamente
                            setActiveTab('em_atendimento');
                            // Aguardar um pouco para a aba mudar antes de recarregar
                            setTimeout(() => {
                              loadMyQueue();
                              handleSelectUser(chat.user_id);
                            }, 100);
                          } else {
                            alert('Erro ao assumir atendimento');
                          }
                        } catch (error) {
                          console.error('Erro:', error);
                          alert('Erro ao assumir atendimento');
                        }
                      }}
                      className="flex-shrink-0 bg-green-500 hover:bg-green-600 text-white text-xs px-3 py-1.5 rounded-lg transition-colors font-medium"
                      title="Assumir atendimento"
                    >
                      ✅ Atender
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Área de Chat e Detalhes - Direita */}
      <div className="flex-1 flex max-h-screen overflow-hidden">
        {!selectedUserId ? (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <MessageSquare className="h-24 w-24 text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-700 mb-2">
                Selecione um atendimento
              </h2>
              <p className="text-gray-500">
                Escolha uma conversa da lista para começar
              </p>
            </div>
          </div>
        ) : loadingUser ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : userData ? (
          <>
          {/* Chat Principal */}
          <div className={`flex flex-col ${showShoppingLists ? 'w-2/3' : 'flex-1'} transition-all duration-300`}>
            {/* Header do Chat */}
            <div className="bg-white border-b border-gray-200 p-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center space-x-4 min-w-0 flex-1">
                  <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                    {userData.user.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 truncate">{userData.user.name}</h2>
                    <div className="flex items-center gap-3 text-sm text-gray-600 flex-wrap">
                      {userData.user.phone && (
                        <span className="flex items-center">
                          <Phone className="h-4 w-4 mr-1" />
                          {userData.user.phone}
                        </span>
                      )}
                      {userData.user.email && (
                        <span className="flex items-center truncate">
                          <Mail className="h-4 w-4 mr-1" />
                          {userData.user.email}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Botão Listas de Compras */}
                  <button
                    onClick={() => setShowShoppingLists(!showShoppingLists)}
                    className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                      showShoppingLists
                        ? 'bg-purple-600 text-white'
                        : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                    }`}
                  >
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    Listas ({userData.shoppingLists?.length || 0})
                  </button>

                  {/* Status do Bot */}
                  <div className="flex items-center space-x-2">
                    {userData.user.bot_enabled ? (
                      <span className="flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                        <Bot className="h-4 w-4 mr-1" />
                        Bot Ativo
                      </span>
                    ) : (
                      <span className="flex items-center px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">
                        <UserCog className="h-4 w-4 mr-1" />
                        Atendimento Manual
                      </span>
                    )}
                  </div>

                  {/* Botão Pausar/Retomar Bot */}
                  <button
                    onClick={toggleBotPause}
                    className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                      botPaused
                        ? 'bg-green-500 hover:bg-green-600 text-white'
                        : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                    }`}
                  >
                    {botPaused ? (
                      <>
                        <PlayCircle className="h-5 w-5 mr-2" />
                        Retomar Bot
                      </>
                    ) : (
                      <>
                        <PauseCircle className="h-5 w-5 mr-2" />
                        Pausar Bot
                      </>
                    )}
                  </button>

                  {/* Botão Fechar Atendimento */}
                  <button
                    onClick={handleCloseChat}
                    className="flex items-center px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                    title="Fechar atendimento (compra finalizada)"
                  >
                    <XCircle className="h-5 w-5 mr-2" />
                    Fechar Atendimento
                  </button>

                  {/* Link para Detalhes
                  <Link
                    to={`/usuarios/${userData.user.id}`}
                    className="flex items-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                  >
                    <ExternalLink className="h-5 w-5 mr-2" />
                    Ver Detalhes
                  </Link> */}
                </div>
              </div>
            </div>

            {/* Mensagens */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
              {userData.messages.length === 0 ? (
                <div className="text-center text-gray-500 mt-8">
                  <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                  <p>Nenhuma mensagem ainda</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {userData.messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.direction === 'incoming' ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-lg p-3 ${
                          msg.direction === 'incoming'
                            ? 'bg-white text-gray-900'
                            : 'bg-blue-500 text-white'
                        }`}
                      >
                        {msg.content.startsWith('/uploads/') ? (() => {
                          // Separa arquivo e caption se houver ';'
                          const [filePath, caption] = msg.content.split(';', 2);
                          // Detectar tipo de arquivo pela extensão
                          const isAudio = /\.(ogg|mp3|wav|m4a|aac)$/i.test(filePath);
                          const isImage = /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(filePath);
                          
                          if (isAudio) {
                            // Renderizar áudio
                            return (
                              <div>
                                <audio
                                  controls
                                  className="w-full max-w-xs"
                                  style={{ maxWidth: '300px' }}
                                >
                                  <source src={`https://crm.brazilianashop.com.br${filePath}`} />
                                  Seu navegador não suporta reprodução de áudio.
                                </audio>
                                {caption && (
                                  <div className="mt-2 p-2 bg-gray-50 rounded text-xs whitespace-pre-wrap border-l-2 border-blue-400" style={{ fontSize: '14px' }}>
                                    <span className="font-semibold text-gray-600">📝 Transcrição:</span>
                                    <div className="mt-1">{caption}</div>
                                  </div>
                                )}
                              </div>
                            );
                          } else if (isImage) {
                            // Renderizar imagem
                            return (
                              <div>
                                <a
                                  href={`https://crm.brazilianashop.com.br${filePath}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <img
                                    src={`https://crm.brazilianashop.com.br${filePath}`}
                                    alt="Imagem enviada"
                                    className="max-w-xs rounded"
                                    style={{ maxHeight: '200px' }}
                                  />
                                </a>
                                {caption && (
                                  <div className="mt-1 text-xs whitespace-pre-wrap" style={{ fontSize: '14px' }}>{caption}</div>
                                )}
                              </div>
                            );
                          } else {
                            // Arquivo desconhecido - mostrar como link
                            return (
                              <div>
                                <a
                                  href={`https://crm.brazilianashop.com.br${filePath}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="underline break-all"
                                >
                                  📎 {filePath.split('/').pop()}
                                </a>
                                {caption && (
                                  <div className="mt-1 text-xs whitespace-pre-wrap" style={{ fontSize: '14px' }}>{caption}</div>
                                )}
                              </div>
                            );
                          }
                        })() : (
                          <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                        )}
                        <p
                          className={`text-xs mt-1 ${
                            msg.direction === 'outgoing' ? 'text-blue-100' : 'text-gray-500'
                          }`}
                        >
                          {formatDate(msg.created_at)} • {msg.direction === 'incoming' ? 'Cliente' : 'Você'}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Input de Mensagem */}
            <div className="bg-white border-t border-gray-200 p-4">
              {/* Alerta de Validação IA */}
              {aiValidationResult && aiValidationResult.show && (
                <div className={`mb-4 p-4 rounded-lg ${
                  aiValidationResult.approved 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <p className={`text-sm whitespace-pre-wrap ${
                    aiValidationResult.approved ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {aiValidationResult.message}
                  </p>
                </div>
              )}

              {/* Botões de Templates de IA */}
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <button
                  type="button"
                  onClick={() => {
                    console.log('Clicou em templates, showTemplates atual:', showTemplates);
                    setShowTemplates(!showTemplates);
                  }}
                  className="flex items-center text-sm text-blue-600 hover:text-blue-800 font-bold mb-2 w-full"
                >
                  <Wand2 className="h-5 w-5 mr-2" />
                  {showTemplates ? '✅ Ocultar' : '✨ Gerar'} Mensagens com IA
                </button>

                {showTemplates && (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                    <button
                      type="button"
                      onClick={() => generateTemplateMessage('welcome')}
                      disabled={generatingTemplate === 'welcome'}
                      className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-2 rounded disabled:opacity-50 flex items-center justify-center"
                      title="Boas-vindas personalizada"
                    >
                      {generatingTemplate === 'welcome' ? <Loader className="h-3 w-3 animate-spin" /> : '👋'} Boas-vindas
                    </button>
                    <button
                      type="button"
                      onClick={() => generateTemplateMessage('freight_simulation')}
                      disabled={generatingTemplate === 'freight_simulation'}
                      className="text-xs bg-green-100 hover:bg-green-200 text-green-700 px-3 py-2 rounded disabled:opacity-50 flex items-center justify-center"
                      title="Simulação de frete"
                    >
                      {generatingTemplate === 'freight_simulation' ? <Loader className="h-3 w-3 animate-spin" /> : '📋'} Frete
                    </button>
                    <button
                      type="button"
                      onClick={() => generateTemplateMessage('payment_confirmation')}
                      disabled={generatingTemplate === 'payment_confirmation'}
                      className="text-xs bg-purple-100 hover:bg-purple-200 text-purple-700 px-3 py-2 rounded disabled:opacity-50 flex items-center justify-center"
                      title="Confirmação de pagamento"
                    >
                      {generatingTemplate === 'payment_confirmation' ? <Loader className="h-3 w-3 animate-spin" /> : '✅'} Pagamento
                    </button>
                    <button
                      type="button"
                      onClick={() => generateTemplateMessage('shipping_update')}
                      disabled={generatingTemplate === 'shipping_update'}
                      className="text-xs bg-orange-100 hover:bg-orange-200 text-orange-700 px-3 py-2 rounded disabled:opacity-50 flex items-center justify-center"
                      title="Atualização de envio"
                    >
                      {generatingTemplate === 'shipping_update' ? <Loader className="h-3 w-3 animate-spin" /> : '🚚'} Envio
                    </button>
                    <button
                      type="button"
                      onClick={() => generateTemplateMessage('priority_packaging')}
                      disabled={generatingTemplate === 'priority_packaging'}
                      className="text-xs bg-yellow-100 hover:bg-yellow-200 text-yellow-700 px-3 py-2 rounded disabled:opacity-50 flex items-center justify-center"
                      title="Embalo prioritário"
                    >
                      {generatingTemplate === 'priority_packaging' ? <Loader className="h-3 w-3 animate-spin" /> : '🚨'} Prioritário
                    </button>
                    <button
                      type="button"
                      onClick={() => generateTemplateMessage('follow_up')}
                      disabled={generatingTemplate === 'follow_up'}
                      className="text-xs bg-indigo-100 hover:bg-indigo-200 text-indigo-700 px-3 py-2 rounded disabled:opacity-50 flex items-center justify-center"
                      title="Follow-up para leads"
                    >
                      {generatingTemplate === 'follow_up' ? <Loader className="h-3 w-3 animate-spin" /> : '📄'} Follow-up
                    </button>
                    <button
                      type="button"
                      onClick={() => generateTemplateMessage('problem_resolution')}
                      disabled={generatingTemplate === 'problem_resolution'}
                      className="text-xs bg-red-100 hover:bg-red-200 text-red-700 px-3 py-2 rounded disabled:opacity-50 flex items-center justify-center"
                      title="Resolução de problemas"
                    >
                      {generatingTemplate === 'problem_resolution' ? <Loader className="h-3 w-3 animate-spin" /> : '🔧'} Problema
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-end gap-2">
                  <textarea
                    ref={textareaRef}
                    value={newMessage}
                    onChange={(e) => {
                      setNewMessage(e.target.value);
                      // Auto-resize
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                    placeholder="Digite sua mensagem..."
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none min-h-[42px] max-h-[200px]"
                    rows={1}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && sendOnEnter) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    disabled={sendingMessage || aiValidating}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={sendingMessage || aiValidating || !newMessage.trim()}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center h-[42px]"
                  >
                    <Send className="h-5 w-5 mr-2" />
                    {aiValidating ? 'Validando...' : sendingMessage ? 'Enviando...' : 'Enviar'}
                  </button>
                </div>

                {/* Opções */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <label className="flex items-center text-xs text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={sendOnEnter}
                        onChange={(e) => setSendOnEnter(e.target.checked)}
                        className="mr-2"
                      />
                      Enviar com Enter
                    </label>
                    {isTrainee && (
                      <div className="flex items-center text-xs text-orange-600">
                        <Bot className="h-3 w-3 mr-1" />
                        Modo Trainee - IA validará suas respostas
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {sendOnEnter ? 'Shift+Enter para quebra de linha' : 'Enter para quebra de linha'}
                  </p>
                </div>
              </div>
            </div>

          </div>
          
          {/* Painel Lateral de Listas de Compras */}
          {showShoppingLists && (
            <div className="w-1/3 border-l border-gray-200 bg-white overflow-y-auto">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <h3 className="font-semibold text-gray-900 flex items-center justify-between">
                  <span className="flex items-center">
                    <ShoppingCart className="h-5 w-5 mr-2 text-purple-600" />
                    Listas de Compras
                  </span>
                  <span className="text-sm font-normal text-gray-600">
                    {userData.shoppingLists?.length || 0} listas
                  </span>
                </h3>
              </div>
              
              <div className="p-4">
                {!userData.shoppingLists || userData.shoppingLists.length === 0 ? (
                  <div className="text-center py-8">
                    <ShoppingCart className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Nenhuma lista de compras</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {userData.shoppingLists.map((list: any) => (
                      <div key={list.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 text-sm">{list.name || 'Lista sem nome'}</h4>
                            <p className="text-xs text-gray-500 mt-1">
                              {list.shopping_list_items?.length || 0} itens
                            </p>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            list.status === 'completed' ? 'bg-green-100 text-green-700' :
                            list.status === 'abandoned' ? 'bg-red-100 text-red-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {list.status === 'completed' ? 'Completa' :
                             list.status === 'abandoned' ? 'Abandonada' :
                             'Ativa'}
                          </span>
                        </div>
                        
                        {list.shopping_list_items && list.shopping_list_items.length > 0 && (
                          <div className="space-y-2 mb-3">
                            {list.shopping_list_items.slice(0, 3).map((item: any) => (
                              <div key={item.id} className="flex items-center gap-2 text-xs bg-gray-50 p-2 rounded">
                                {item.product_image && (
                                  <img 
                                    src={item.product_image} 
                                    alt={item.product_name}
                                    className="w-8 h-8 object-cover rounded"
                                  />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900 truncate">{item.product_name}</p>
                                  <p className="text-gray-600">Qtd: {item.quantity} • ${item.product_price}</p>
                                </div>
                              </div>
                            ))}
                            {list.shopping_list_items.length > 3 && (
                              <p className="text-xs text-gray-500 text-center">
                                +{list.shopping_list_items.length - 3} itens
                              </p>
                            )}
                          </div>
                        )}
                        
                        <div className="flex flex-col gap-2">
                          {/* Botão Expandir/Recolher */}
                          <button
                            onClick={() => toggleListExpansion(list.id)}
                            className="w-full text-xs py-2 px-3 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors flex items-center justify-center gap-1"
                          >
                            {expandedLists.includes(list.id) ? (
                              <><ChevronUp className="h-3 w-3" /> Recolher</>
                            ) : (
                              <><ChevronDown className="h-3 w-3" /> Ver Todos os Itens</>
                            )}
                          </button>
                          
                          {/* Ações da Lista */}
                          <div className="grid grid-cols-2 gap-2">
                            {/* Link de Pagamento */}
                            {showCustomAmount === list.id ? (
                              <div className="col-span-2 space-y-2">
                                <div className="flex gap-2">
                                  <input
                                    type="number"
                                    value={customAmount || ''}
                                    onChange={(e) => setCustomAmount(parseFloat(e.target.value) || 0)}
                                    placeholder="Valor em BRL"
                                    className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded"
                                  />
                                  <button
                                    onClick={() => createPaymentLink(list.id)}
                                    disabled={creatingPaymentLink === list.id}
                                    className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                                  >
                                    {creatingPaymentLink === list.id ? '⏳' : '✓'}
                                  </button>
                                  <button
                                    onClick={() => setShowCustomAmount(null)}
                                    className="px-3 py-1 bg-gray-200 text-gray-700 text-xs rounded hover:bg-gray-300"
                                  >
                                    ✕
                                  </button>
                                </div>
                                <p className="text-xs text-gray-500">
                                  Total USD: {formatCurrency(calculateListTotal(list.shopping_list_items || []))}
                                </p>
                              </div>
                            ) : !list.payment_link ? (
                              <button
                                onClick={() => openPaymentLinkForm(list.id, list.shopping_list_items || [])}
                                className="text-xs py-2 px-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                              >
                                💳 Pagamento
                              </button>
                            ) : (
                              <a
                                href={list.payment_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs py-2 px-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-center"
                              >
                                🔗 Ver Link
                              </a>
                            )}
                            
                            {/* Marcar como Concluída */}
                            {list.status !== 'completed' && (
                              <button
                                onClick={() => updateListStatus(list.id, 'completed')}
                                disabled={updatingListStatus === list.id}
                                className="text-xs py-2 px-2 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors disabled:opacity-50"
                              >
                                {updatingListStatus === list.id ? '...' : '✅ Concluir'}
                              </button>
                            )}
                            
                            {/* Marcar como Abandonada */}
                            {list.status === 'active' && (
                              <button
                                onClick={() => updateListStatus(list.id, 'abandoned')}
                                disabled={updatingListStatus === list.id}
                                className="text-xs py-2 px-2 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors disabled:opacity-50"
                              >
                                {updatingListStatus === list.id ? '...' : '❌ Abandonar'}
                              </button>
                            )}
                            
                            {/* Reativar */}
                            {list.status !== 'active' && (
                              <button
                                onClick={() => updateListStatus(list.id, 'active')}
                                disabled={updatingListStatus === list.id}
                                className="text-xs py-2 px-2 bg-orange-100 text-orange-700 rounded hover:bg-orange-200 transition-colors disabled:opacity-50"
                              >
                                {updatingListStatus === list.id ? '...' : '🔄 Reativar'}
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {/* Lista Expandida - Todos os Itens */}
                        {expandedLists.includes(list.id) && list.shopping_list_items && list.shopping_list_items.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <div className="space-y-2">
                              {list.shopping_list_items.map((item: any) => (
                                <div key={item.id} className="flex items-start gap-2 text-xs bg-white p-2 rounded border border-gray-200">
                                  {item.product_image && (
                                    <img 
                                      src={item.product_image} 
                                      alt={item.product_name}
                                      className="w-12 h-12 object-cover rounded flex-shrink-0"
                                    />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-gray-900 truncate">{item.product_name}</p>
                                    <p className="text-gray-600">Qtd: {item.quantity}</p>
                                    <p className="text-gray-600">Preço: {formatCurrency(item.product_price)}</p>
                                    <p className="font-semibold text-blue-600">Total: {formatCurrency(item.product_price * item.quantity)}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="mt-3 pt-3 border-t border-gray-300 bg-gray-50 p-2 rounded">
                              <div className="flex justify-between items-center">
                                <span className="font-bold text-sm text-gray-900">Total da Lista:</span>
                                <span className="font-bold text-lg text-green-600">
                                  {formatCurrency(calculateListTotal(list.shopping_list_items))}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          </>
        ) : null}
      </div>
    </div>
  );
}
